import React, { Suspense, useContext } from 'react';
import cx from 'classnames';

import { useBoardLists } from '@trello/business-logic-react/board';
import { ChunkLoadErrorBoundary, ErrorBoundary } from '@trello/error-reporting';
import { BoardListTestIds } from '@trello/test-ids';

import { ModelCache } from 'app/scripts/db/model-cache';
import type { Board } from 'app/scripts/models/board';
import { InlineListComposerView } from 'app/scripts/views/list/InlineListComposerView';
import { ListView } from 'app/scripts/views/list/ListView';
import { BackboneViewAsComponent } from 'app/src/components/BackboneViewAsComponent';
import { BoardViewContext } from 'app/src/components/BoardViewContext';
import { SingleBoardViewProvider } from 'app/src/components/BoardViewContext/SingleBoardViewProvider';

import styles from './ListList.less';

export const ListListUnWrapped = ({ idBoard }: { idBoard: string }) => {
  const { lists } = useBoardLists({ idBoard });
  const { canEditBoard } = useContext(BoardViewContext);
  const maxVisibleLists = Math.max(3, Math.ceil(window.innerWidth / 270));

  return (
    <div
      id="board"
      data-drag-scroll-enabled={true}
      data-testid={BoardListTestIds.Lists}
      className={cx(
        styles.listList,
        canEditBoard(idBoard) && 'js-list-sortable',
        'u-fancy-scrollbar js-no-higher-edits',
      )}
    >
      {lists.map((list, index) => (
        <BackboneViewAsComponent<ListView['options'], Board, ListView>
          key={list.id}
          View={ListView}
          model={ModelCache.findOne('List', 'id', list.id)}
          options={{
            deferCards: index >= maxVisibleLists,
            renderedFromReact: true,
          }}
          className={cx(
            index === 0 && styles.firstList,
            index === lists.length - 1 && styles.lastList,
          )}
        />
      ))}
      <BackboneViewAsComponent<
        InlineListComposerView['options'],
        Board,
        InlineListComposerView
      >
        View={InlineListComposerView}
        model={ModelCache.findOne('Board', 'id', idBoard)}
        options={void 0}
      />
    </div>
  );
};

export const ListList = ({
  idBoard,
  navigateToCard,
}: {
  idBoard: string;
  navigateToCard: (id: string) => void;
}) => {
  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-workflowers',
        feature: 'List List',
      }}
    >
      <Suspense fallback={null}>
        <ChunkLoadErrorBoundary fallback={null}>
          <SingleBoardViewProvider
            idBoard={idBoard}
            isDateBasedView={false}
            navigateToCard={navigateToCard}
            includeChecklistItems={false}
          >
            <ListListUnWrapped idBoard={idBoard} />
          </SingleBoardViewProvider>
        </ChunkLoadErrorBoundary>
      </Suspense>
    </ErrorBoundary>
  );
};
